import { Link } from 'react-router-dom';
import { ShoppingCart, User, ChevronDown, Menu, Sparkles } from 'lucide-react';
import { useCategories, useAllProducts } from '@/hooks/useProducts';
import { useCart } from '@/hooks/useCart';
import { useDisplayPriceSum } from '@/hooks/useDisplayPrice';
import { useStorefront, SINGLE_LANGUAGE_STOREFRONTS } from '@/hooks/useStorefront';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage, LOCALES, LOCALE_META, type Locale } from '@/hooks/useLanguage';
import { useTranslations } from '@/hooks/useTranslations';
import { useCategoryTranslations, useProductTranslations, applyCategoryTranslation, applyProductTranslation } from '@/hooks/useContentTranslation';
import { useState, useRef, useCallback, useMemo } from 'react';
import SocialIcon from '@/components/SocialIcon';
import MobileMenu from '@/components/MobileMenu';
import CategoryMegaMenu from '@/components/CategoryMegaMenu';
import MoreServicesMegaMenu from '@/components/MoreServicesMegaMenu';
import { useMyLoyalty } from '@/hooks/useLoyalty';
import { formatPrice, formatPriceForCurrency, currencyCode } from '@/lib/formatPrice';
import PromoBar from '@/components/promo/PromoBar';

const mainSlugs = ['instagram', 'tiktok', 'youtube', 'twitter', 'spotify'];

export default function Header() {
  const { toggleCart, itemCount, items, discountThreshold, discountPercent } = useCart();
  // Header cart amount uses the same FX/override-aware price path as PDP,
  // SideCart and CheckoutPage. CH fastpath: byte-identical to
  // formatPrice(subtotalCHF * (1 - discount)).
  const { currency, storefront } = useStorefront();
  const cartDisplay = useDisplayPriceSum(items.map(i => ({ chf: i.price, variationId: i.variationId })));
  const rawCHFSubtotal = items.reduce((s, i) => s + i.price, 0);
  const hasCartDiscount = rawCHFSubtotal >= discountThreshold;
  const displayTotalAmount = cartDisplay.sum
    ? (hasCartDiscount ? cartDisplay.sum.amount * (1 - discountPercent / 100) : cartDisplay.sum.amount)
    : 0;
  const totalFormatted = cartDisplay.sum
    ? formatPriceForCurrency(displayTotalAmount, currency)
    : null;
  const { categories: rawCategories } = useCategories();
  const { products: rawProducts } = useAllProducts();
  const { user } = useAuth();
  const { locale, switchLocale, localePath } = useLanguage();
  const { t } = useTranslations();
  const { overview: loyaltyOverview } = useMyLoyalty();
  const [langOpen, setLangOpen] = useState(false);
  const currentLangMeta = LOCALE_META[locale];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  // Batch translate categories & products
  const categoryIds = useMemo(() => rawCategories.map(c => c.id), [rawCategories]);
  const productIds = useMemo(() => rawProducts.map(p => p.id), [rawProducts]);
  const catOverlays = useCategoryTranslations(categoryIds);
  const prodOverlays = useProductTranslations(productIds);

  const categories = useMemo(
    () => rawCategories.map(c => applyCategoryTranslation(c, catOverlays[c.id] || null)),
    [rawCategories, catOverlays]
  );

  // Build title + slug override maps: productId → translated value
  const { titleOverrides, slugOverrides } = useMemo(() => {
    const titles: Record<string, string> = {};
    const slugs: Record<string, string> = {};
    for (const p of rawProducts) {
      const overlay = prodOverlays[p.id];
      if (overlay?.title?.trim()) titles[p.id] = overlay.title;
      if (overlay?.slug?.trim()) slugs[p.id] = overlay.slug;
    }
    return { titleOverrides: titles, slugOverrides: slugs };
  }, [rawProducts, prodOverlays]);

  // 8f §3 — Sicherheitsnetz: eine Kategorie ohne lieferbare Produkte führt auf
  // eine leere Seite und gehört deshalb in keine Navigation. Heute hat jede
  // der 12 Kategorien Produkte, der Filter ist also auf allen Storefronts
  // (CH/AT/IT/AL) ein No-op — er greift erst, wenn eine Kategorie leer wird.
  const visibleCategories = useMemo(
    () => categories.filter(c => rawProducts.some(p => p.category_id === c.id)),
    [categories, rawProducts]
  );

  const mainCategories = visibleCategories.filter(c => mainSlugs.includes(c.slug));
  const moreCategories = visibleCategories.filter(c => !mainSlugs.includes(c.slug) && c.slug !== 'twitch');
  const twitch = visibleCategories.find(c => c.slug === 'twitch');

  const handleCategoryEnter = useCallback((categoryId: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredCategory(categoryId);
  }, []);

  const handleCategoryLeave = useCallback(() => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredCategory(null);
    }, 150);
  }, []);

  const getCategoryProducts = useCallback((categoryId: string) => {
    return rawProducts.filter(p => p.category_id === categoryId);
  }, [rawProducts]);

  // Translated announcement bar
  const announcementTexts: Record<Locale, { left: string; right: string }> = {
    de: { left: '⚡ Schnelle Resultate / Günstige Preise ⚡', right: `10% Rabatt bei einem Einkauf ab 20 ${currencyCode()} 🎁` },
    en: { left: '⚡ Fast Results / Low Prices ⚡', right: `10% discount on orders over 20 ${currencyCode()} 🎁` },
    it: { left: '⚡ Risultati rapidi / Prezzi bassi ⚡', right: `10% di sconto per ordini superiori a 20 ${currencyCode()} 🎁` },
    fr: { left: '⚡ Résultats rapides / Prix bas ⚡', right: `10% de réduction dès 20 ${currencyCode()} d'achat 🎁` },
    sq: { left: '⚡ Rezultate të shpejta / Çmime të lira ⚡', right: `10% zbritje për porositë mbi 20 ${currencyCode()} 🎁` },
  };

  const moreServicesTexts: Record<Locale, string> = {
    de: 'Mehr Services', en: 'More Services', it: 'Altri servizi', fr: 'Plus de services', sq: 'Më shumë shërbime',
  };

  return (
    <>
      <PromoBar />
      {/* Announcement Bar – fixed min-height prevents CLS during font load */}
      <div className="announcement-bar flex min-h-[36px] items-center justify-center px-4 text-center text-sm font-medium tracking-wide">
        <span>{announcementTexts[locale].left}</span>
        <span className="mx-4 hidden sm:inline">|</span>
        <span className="hidden sm:inline">{announcementTexts[locale].right}</span>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-[1200] bg-[hsl(var(--header-bg))] text-[hsl(var(--header-foreground))]">
        <div className="container flex h-14 items-center justify-between md:h-16">
          {/* Left – Hamburger (mobile) + Language (desktop) */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="rounded-lg p-2 transition-colors hover:bg-white/10 md:hidden"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Sprachumschalter nur auf Mehrsprach-Storefronts (heute nur CH).
                Listengesteuert über SINGLE_LANGUAGE_STOREFRONTS (AT, IT, AL, …). */}
            {!SINGLE_LANGUAGE_STOREFRONTS.includes(storefront) && (
            <div className="relative z-[1300] hidden md:block">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLangOpen(!langOpen);
                }}
                className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-white/20"
              >
                <img src={`/flags/${currentLangMeta.flagCode}.svg`} width="20" height="15" alt={currentLangMeta.label} className="inline-block rounded-[2px]" />
                <span>{currentLangMeta.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-[60]" onClick={() => setLangOpen(false)} />
                  <div className="absolute left-0 top-full z-[1400] mt-2 w-44 rounded-xl border bg-card p-1 shadow-lg text-foreground">
                    {LOCALES.map(loc => {
                      const meta = LOCALE_META[loc];
                      return (
                        <button
                          key={loc}
                          onClick={() => {
                            switchLocale(loc);
                            setLangOpen(false);
                          }}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors hover:bg-accent ${loc === locale ? 'bg-accent' : ''}`}
                        >
                          <img src={`/flags/${meta.flagCode}.svg`} width="20" height="15" alt={meta.label} className="inline-block rounded-[2px]" />
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            )}
          </div>

          {/* Center – Logo
              Fixed CSS height + width via aspect-ratio prevents CLS before
              the image is decoded. Original asset is 1157×284 (~4.07:1). */}
          <Link to={localePath('/')} className="absolute left-1/2 -translate-x-1/2">
            <img
              src="/logo-white.png"
              alt="BigFollow"
              width="114"
              height="28"
              decoding="async"
              fetchPriority="high"
              className="h-7 w-[114px] md:h-8 md:w-[130px]"
              style={{ aspectRatio: '1157 / 284' }}
            />
          </Link>

          {/* Right – Cart (+ User on desktop) */}
          <div className="flex items-center gap-1 md:gap-3">
            {user && loyaltyOverview?.authenticated && loyaltyOverview.has_account && loyaltyOverview.balance > 0 && (
              <Link
                to={`${localePath('/konto')}?tab=loyalty`}
                className="hidden items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold transition-colors hover:bg-white/20 md:inline-flex"
                title={t('loyalty.badge_title').replace('{points}', String(loyaltyOverview.balance))}
                aria-label={t('loyalty.badge_title').replace('{points}', String(loyaltyOverview.balance))}
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span className="font-mono">{loyaltyOverview.balance.toLocaleString('de-CH')}</span>
              </Link>
            )}
            <Link
              to={user ? localePath('/konto') : localePath('/anmelden')}
              className="hidden rounded-lg p-2 transition-colors hover:bg-white/10 md:block"
              aria-label={user ? t('nav.account') : t('nav.login')}
            >
              <User className="h-5 w-5" />
            </Link>
            <button
              onClick={toggleCart}
              className="relative flex items-center gap-1.5 rounded-lg px-2 py-2 text-sm font-medium transition-colors hover:bg-white/10 md:gap-2 md:px-3"
            >
              <span className="font-mono text-xs md:text-sm">
                {totalFormatted ?? (
                  <span className="inline-block h-4 w-14 animate-pulse rounded bg-white/20 align-middle" aria-hidden />
                )}
              </span>
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Category Nav – desktop only */}
        <nav className="relative z-[1100] hidden overflow-visible border-y border-border bg-white text-foreground shadow-sm md:block">
          <div className="container flex flex-wrap items-center justify-center gap-1 py-2">
            {mainCategories.map(cat => (
              <div
                key={cat.id}
                className="relative"
                onMouseEnter={() => handleCategoryEnter(cat.id)}
                onMouseLeave={handleCategoryLeave}
              >
                <Link
                  to={localePath(`/${cat.slug}`)}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
                >
                  <SocialIcon slug={cat.slug} className="h-4 w-4" />
                  {cat.name}
                  <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform ${hoveredCategory === cat.id ? 'rotate-180' : ''}`} />
                </Link>

                {hoveredCategory === cat.id && (
                  <CategoryMegaMenu
                    categoryName={cat.name}
                    categorySlug={cat.slug}
                    products={getCategoryProducts(cat.id)}
                    titleOverrides={titleOverrides}
                    slugOverrides={slugOverrides}
                    onClose={() => setHoveredCategory(null)}
                  />
                )}
              </div>
            ))}

            {twitch && (
              <Link
                to={localePath(`/${twitch.slug}`)}
                className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
              >
                <SocialIcon slug={twitch.slug} className="h-4 w-4" /> {twitch.name}
              </Link>
            )}

            {moreCategories.length > 0 && (
              <div
                className="relative"
                onMouseEnter={() => handleCategoryEnter('__more__')}
                onMouseLeave={handleCategoryLeave}
              >
                <button className="flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted">
                  {moreServicesTexts[locale]}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${hoveredCategory === '__more__' ? 'rotate-180' : ''}`} />
                </button>
                {hoveredCategory === '__more__' && (
                  <MoreServicesMegaMenu
                    categories={moreCategories}
                    products={rawProducts}
                    titleOverrides={titleOverrides}
                    slugOverrides={slugOverrides}
                    onClose={() => setHoveredCategory(null)}
                  />
                )}
              </div>
            )}
          </div>
        </nav>

        {/* Mobile: Discount bar – reserved height to avoid CLS */}
        <div className="flex min-h-[28px] items-center justify-center border-b border-border bg-white px-4 text-center text-xs font-medium text-foreground md:hidden">
          {announcementTexts[locale].right}
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        categories={visibleCategories}
        products={rawProducts}
        titleOverrides={titleOverrides}
        slugOverrides={slugOverrides}
        currentLang={{ code: locale, label: currentLangMeta.label, flag: '', flagCode: currentLangMeta.flagCode.toLowerCase() }}
        onLangChange={(lang) => switchLocale(lang.code as Locale)}
      />
    </>
  );
}
